import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { fetchTaskFilterOptions } from "../../../api/tasks/tasksApi";
import { TaskWorklistFilters } from "../../../api/tasks/taskTypes";
import { FilterSection, FilterValues } from "../../../data-model/filterValues";
import { OrganizationType } from "../../../enums/organizationTypes";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import {
  buildTaskFilterSections,
  TasksFilterSelection,
  TASKS_FILTER_NAME,
  toFilterValues,
  toSelection,
  toServerFilters,
} from "./tasksFilterHelpers";

interface UseTasksFiltersArgs {
  /** Setter from useTasksData that triggers a server refetch. */
  applyFilters: (filters: Partial<TaskWorklistFilters>) => void;
  /** URL-seeded selection used to hydrate the chips/dialog on first load. */
  initialSelection: TasksFilterSelection;
  /** Called with the new selection whenever filters change (persists to URL). */
  onSelectionChange: (selection: TasksFilterSelection) => void;
}

interface UseTasksFiltersResult {
  filterSections: FilterSection[];
  onFilterApply: (data: FilterValues) => void;
}

/**
 * Fetch distinct facet values and expose them as CustomTable filter sections,
 * mapping an applied FilterValues selection back to server-side worklist filters.
 * Also mirrors the selection into Redux (chips/count/dialog) and the URL.
 *
 * @param args - applyFilters setter, URL-seeded selection, and change callback
 * @returns Filter sections for the dialog + the apply handler
 */
export const useTasksFilters = ({
  applyFilters,
  initialSelection,
  onSelectionChange,
}: UseTasksFiltersArgs): UseTasksFiltersResult => {
  const dispatch = useDispatch();
  const [filterSections, setFilterSections] = useState<FilterSection[]>([]);
  const hydratedRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchTaskFilterOptions(
      OrganizationType.ASTERA_PRIMARY_CARE,
      controller.signal,
    )
      .then((options) => setFilterSections(buildTaskFilterSections(options)))
      .catch(() => setFilterSections([]));
    return () => controller.abort();
  }, []);

  // Hydrate the chips/dialog from the URL-seeded selection once the sections
  // (and their labels) are available. Runs at most once.
  useEffect(() => {
    if (hydratedRef.current || filterSections.length === 0) return;
    if (Object.keys(initialSelection).length > 0) {
      dispatch(setFilter(toFilterValues(initialSelection, filterSections)));
    }
    hydratedRef.current = true;
  }, [dispatch, filterSections, initialSelection]);

  const onFilterApply = useCallback(
    (data: FilterValues) => {
      const selection = toSelection(data.values);
      applyFilters(toServerFilters(selection));
      dispatch(setFilter({ ...data, name: TASKS_FILTER_NAME }));
      onSelectionChange(selection);
    },
    [applyFilters, dispatch, onSelectionChange],
  );

  return { filterSections, onFilterApply };
};
